import React, { createContext, useContext, useState, useEffect } from 'react';
import requests from '../services/httpServices';

// Default R-Link branding
const DEFAULT_BRANDING = {
  companyName: 'R-Link',
  companyLogo: null, // Will use default logo from images
  companyIcon: null,
  supportEmail: 'support@r-link.com',
  isWhiteLabel: false,
};

const DEFAULT_TITLE = 'R-Link: Secure, Rewarding Video Conferencing';
const DEFAULT_FAVICON = '/images/favicon.png';
const MAIN_DOMAINS = ['studio.r-link.com', 'studioa.r-link.com', 'localhost', '127.0.0.1', 'studiodev.r-link.com'];
const WL_CACHE_KEY = 'wl_branding';

// Check if current hostname is a whitelabel domain
const isWhiteLabelHost = () => {
  const hostname = window.location.hostname;
  return !MAIN_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d));
};

// Try to load cached branding synchronously (prevents flash)
const getCachedBranding = () => {
  if (!isWhiteLabelHost()) return null;
  try {
    const cached = JSON.parse(localStorage.getItem(WL_CACHE_KEY));
    if (cached && cached.isWhiteLabel) return cached;
  } catch (e) { /* ignore */ }
  return null;
};

// Create context
const WhiteLabelContext = createContext();

// Custom hook to use the context
export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
};

// Provider component
export const WhiteLabelProvider = ({ children }) => {
  const cached = getCachedBranding();
  const [branding, setBranding] = useState(cached || DEFAULT_BRANDING);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWhiteLabelConfig = async () => {
      try {
        if (!cached) setLoading(true);
        setError(null);

        const hostname = window.location.hostname;

        // Skip whitelabel lookup for main R-Link domains
        if (!isWhiteLabelHost()) {
          setBranding(DEFAULT_BRANDING);
          localStorage.removeItem(WL_CACHE_KEY);
          setLoading(false);
          return;
        }

        // Fetch whitelabel config by domain
        console.log('[WhiteLabel] Fetching config for hostname:', hostname);
        const response = await requests.get(`/users/whitelabel/domain/${hostname}`);
        console.log('[WhiteLabel] API response:', response);

        if (response.success && response.data) {
          const newBranding = {
            companyName: response.data.companyName || 'R-Link',
            companyLogo: response.data.companyLogo || null,
            companyIcon: response.data.companyIcon || null,
            supportEmail: response.data.supportEmail || 'support@r-link.com',
            domain: response.data.domain,
            isWhiteLabel: true,
          };
          setBranding(newBranding);
          // Cache for instant load on next visit
          try { localStorage.setItem(WL_CACHE_KEY, JSON.stringify(newBranding)); } catch (e) { /* ignore */ }
        } else {
          setBranding(DEFAULT_BRANDING);
          localStorage.removeItem(WL_CACHE_KEY);
        }
      } catch (err) {
        console.error('Failed to fetch whitelabel config:', err);
        if (!cached) setBranding(DEFAULT_BRANDING);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWhiteLabelConfig();
  }, []);

  // Dynamic page title, OG tags, and favicon based on branding
  useEffect(() => {
    if (loading) return;

    const title = (branding.isWhiteLabel && branding.companyName)
      ? `${branding.companyName}: Secure, Rewarding Video Conferencing`
      : DEFAULT_TITLE;

    // Update page title
    document.title = title;

    // Update OG meta tags
    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('og:title', title);
    setMeta('og:site_name', branding.isWhiteLabel && branding.companyName ? branding.companyName : 'R-Link');

    // Update favicon
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (branding.isWhiteLabel && branding.companyIcon) {
      link.href = branding.companyIcon;
    } else {
      link.href = DEFAULT_FAVICON;
    }
  }, [branding, loading]);

  const value = {
    branding,
    loading,
    error,
    isWhiteLabel: branding.isWhiteLabel,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export default WhiteLabelContext;
